import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { variantLabelOf } from "../lib/variants";
import { paymentMethodLabel, PAYMENT_STATUS_LABELS } from "../lib/orderLabels";

export default function OrderConfirmation() {
    const { orderNumber } = useParams();
    const [order, setOrder] = useState(null);
    const [whatsappNumber, setWhatsappNumber] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        Promise.all([
            api.get(`/orders/${orderNumber}`),
            api.get(`/info/whatsapp`).catch(() => ({ data: { whatsapp: "" } })),
        ])
            .then(([orderRes, waRes]) => {
                setOrder(orderRes.data);
                setWhatsappNumber(waRes.data?.whatsapp || "");
            })
            .catch(() => setError("تعذر تحميل بيانات الطلب"))
            .finally(() => setLoading(false));
    }, [orderNumber]);

    const buildWhatsAppMessage = () => {
        if (!order) return "";
        const lines = [];
        lines.push("طلب جديد من موقع تسالي سكة 10");
        lines.push("");
        lines.push(`رقم الطلب: ${order.order_number}`);
        lines.push(
            `نوع الطلب: ${order.order_type === "car" ? "توصيل للسيارة" : "توصيل للموقع"}`
        );
        lines.push(`الاسم: ${order.customer_name}`);
        lines.push(`الجوال: ${order.phone}`);
        if (order.order_type === "car" && order.car_info) {
            lines.push(
                `السيارة: ${order.car_info.car_type} - ${order.car_info.car_color} - لوحة ${order.car_info.plate_number}`
            );
        }
        if (order.order_type === "location" && order.location_info) {
            lines.push(`العنوان: ${order.location_info.address}`);
            if (order.location_info.location_link) {
                lines.push(`الموقع: ${order.location_info.location_link}`);
            }
        }
        lines.push("");
        lines.push("الأصناف:");
        order.items.forEach((it) => {
            const v = it.variant ? ` (${variantLabelOf(it.category, it.variant)})` : "";
            const n = it.notes ? ` - ${it.notes}` : "";
            lines.push(`• ${it.name}${v} × ${it.quantity} = ${(it.price * it.quantity).toFixed(2)} ر.س${n}`);
        });
        lines.push("");
        lines.push(`الإجمالي: ${order.total.toFixed(2)} ر.س`);
        lines.push(`طريقة الدفع: ${paymentMethodLabel(order.payment_method)}`);
        lines.push(`حالة الدفع: ${PAYMENT_STATUS_LABELS[order.payment_status] || "-"}`);
        if (order.notes) lines.push(`ملاحظات: ${order.notes}`);
        return lines.join("\n");
    };

    const waHref = whatsappNumber
        ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(buildWhatsAppMessage())}`
        : "#";

    if (loading) {
        return (
            <div className="max-w-md mx-auto px-4 py-12 text-center text-text-muted" data-testid="order-loading">
                <i className="fa-solid fa-spinner fa-spin text-3xl text-brand" />
                <p className="mt-3">جاري تحميل الطلب...</p>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="max-w-md mx-auto px-4 py-12 text-center" data-testid="order-error">
                <p className="text-red-600 font-bold">{error || "خطأ"}</p>
                <Link to="/" className="btn-brand inline-block mt-4">العودة للرئيسية</Link>
            </div>
        );
    }

    if (order.payment_method === "online" && order.payment_status !== "paid") {
        return <Navigate to={`/pay/${order.order_number}`} replace />;
    }

    return (
        <div data-testid="order-confirmation-page" className="max-w-md mx-auto md:max-w-2xl px-4 py-8">
            <div className="surface-card p-6 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-[var(--mint)] flex items-center justify-center text-white text-3xl mb-3">
                    <i className="fa-solid fa-check" />
                </div>
                <h1 className="text-2xl font-black text-text-main">تم الدفع واستلام طلبك بنجاح</h1>
                <p className="text-text-muted mt-2 text-sm">
                    {order.order_type === "car"
                        ? "سيتم تجهيز طلبك وتوصيله لسيارتك قريباً"
                        : "سيتم تجهيز طلبك وتوصيله لموقعك قريباً"}
                </p>
                <div className="mt-4 inline-block bg-[var(--bg)] rounded-xl px-4 py-2 border border-[var(--border-subtle)]">
                    <div className="text-text-muted text-xs">رقم الطلب</div>
                    <div className="text-brand font-black text-2xl" data-testid="order-number">
                        {order.order_number}
                    </div>
                </div>
            </div>

            <div className="surface-card p-5 mt-4">
                <h2 className="font-extrabold text-text-main mb-3 flex items-center gap-2">
                    <i className={`fa-solid ${order.order_type === "car" ? "fa-car" : "fa-location-dot"} text-brand`} />
                    {order.order_type === "car" ? "توصيل للسيارة" : "توصيل للموقع"}
                </h2>
                <div className="text-sm space-y-1 text-text-main">
                    <Row label="الاسم" value={order.customer_name} />
                    <Row label="الجوال" value={order.phone} />
                    {order.order_type === "car" && order.car_info && (
                        <>
                            <Row label="نوع السيارة" value={order.car_info.car_type} />
                            <Row label="لون السيارة" value={order.car_info.car_color} />
                            <Row label="اللوحة" value={order.car_info.plate_number} />
                        </>
                    )}
                    {order.order_type === "location" && order.location_info && (
                        <>
                            <Row label="العنوان" value={order.location_info.address} />
                            {order.location_info.location_link && (
                                <Row label="رابط الموقع" value={order.location_info.location_link} truncate />
                            )}
                        </>
                    )}
                    <Row label="طريقة الدفع" value={paymentMethodLabel(order.payment_method)} />
                    {order.payment_method === "online" && (
                        <Row label="حالة الدفع" value={PAYMENT_STATUS_LABELS[order.payment_status]} testId="confirmation-payment-status" />
                    )}
                    {order.notes && <Row label="ملاحظات" value={order.notes} />}
                </div>
            </div>

            <div className="surface-card p-5 mt-4">
                <h2 className="font-extrabold text-text-main mb-3">ملخص الأصناف</h2>
                <div className="space-y-2">
                    {order.items.map((it, idx) => (
                        <div key={idx} className="flex justify-between text-sm" data-testid={`summary-line-${idx}`}>
                            <div className="text-text-main">
                                <span className="font-bold">{it.name}</span>
                                {it.variant && (
                                    <span className="text-text-muted text-xs ms-1">
                                        ({variantLabelOf(it.category, it.variant)})
                                    </span>
                                )}
                                <span className="text-text-muted ms-1">× {it.quantity}</span>
                                {it.notes && (
                                    <div className="text-text-muted text-xs">ملاحظة: {it.notes}</div>
                                )}
                            </div>
                            <div className="font-bold text-text-main">
                                {(it.price * it.quantity).toFixed(2)} ر.س
                            </div>
                        </div>
                    ))}
                </div>
                <div className="border-t border-[var(--border-subtle)] mt-3 pt-3 flex justify-between items-center">
                    <span className="font-bold text-text-muted">الإجمالي</span>
                    <span className="text-brand font-black text-xl" data-testid="confirmation-total">
                        {order.total.toFixed(2)} ر.س
                    </span>
                </div>
            </div>

            <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-send"
                onClick={(e) => {
                    if (!whatsappNumber) {
                        e.preventDefault();
                        alert("لم يتم إعداد رقم الواتساب بعد");
                    }
                }}
                className="bg-whatsapp text-white rounded-2xl block text-center font-extrabold text-lg py-4 mt-5 hover:opacity-90 transition"
            >
                <i className="fa-brands fa-whatsapp ms-2 text-xl" /> إرسال الطلب عبر الواتساب
            </a>

            <Link
                to="/"
                data-testid="back-home"
                className="block text-center text-text-muted mt-4 font-bold hover:text-brand transition"
            >
                ← العودة للرئيسية
            </Link>
        </div>
    );
}

function Row({ label, value, truncate, testId }) {
    return (
        <div className="flex gap-2" data-testid={testId}>
            <span className="text-text-muted min-w-[90px]">{label}:</span>
            <span className={`font-bold flex-1 ${truncate ? "truncate" : ""}`} dir="auto">
                {value}
            </span>
        </div>
    );
}
